import React from "react";
import { Clock, ArrowRight, Broadcast } from "@phosphor-icons/react";
import { TYPE_ICON, ratePill, relativeTime } from "./deliveryHealthHelpers";
import StatBar from "./StatBar";

function StatCell({ stats }) {
  return (
    <div className="w-24">
      <div className="flex justify-between text-[10px]">
        <span className="text-emerald-800">{stats.success}</span>
        <span className="text-red-800">{stats.failure}</span>
      </div>
      <StatBar success={stats.success} failure={stats.failure} retrying={stats.retrying} />
      <div className="text-[9px] text-zinc-500 mt-0.5">{stats.total} total</div>
    </div>
  );
}

/** Per-destination performance table. Click a row to open the drill modal. */
export default function DeliveryHealthTable({ rows, onDrill }) {
  return (
    <div className="brut-card overflow-x-auto">
      <table className="w-full text-sm font-mono min-w-[1000px]">
        <thead>
          <tr className="border-b border-black text-[10px] uppercase tracking-widest">
            <th className="py-2 px-4 text-left">Destination</th>
            <th className="py-2 px-4 text-left">24h</th>
            <th className="py-2 px-4 text-left">Rate</th>
            <th className="py-2 px-4 text-left">7d</th>
            <th className="py-2 px-4 text-left">Rate</th>
            <th className="py-2 px-4 text-left">Pending</th>
            <th className="py-2 px-4 text-left">Last</th>
            <th className="py-2 px-4 text-left"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const Icon = TYPE_ICON[r.type] || Broadcast;
            const rate24 = ratePill(r.stats_24h.success_rate);
            const rate7d = ratePill(r.stats_7d.success_rate);
            return (
              <tr key={r.destination_id}
                className="border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer"
                onClick={() => onDrill(r.destination_id)}
                data-testid={`health-row-${r.destination_id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Icon size={14} weight="bold" />
                    <div>
                      <div className="font-semibold">{r.name || "(unnamed)"}</div>
                      <div className="text-[10px] text-zinc-500">
                        {r.type}{r.is_active ? "" : " · inactive"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4"><StatCell stats={r.stats_24h} /></td>
                <td className="py-3 px-4">
                  <span
                    className={`inline-block border px-2 py-0.5 text-[11px] font-bold ${rate24.color} ${rate24.bg}`}
                    data-testid={`health-rate-24h-${r.destination_id}`}
                  >
                    {rate24.label}
                  </span>
                </td>
                <td className="py-3 px-4"><StatCell stats={r.stats_7d} /></td>
                <td className="py-3 px-4">
                  <span className={`inline-block border px-2 py-0.5 text-[11px] font-bold ${rate7d.color} ${rate7d.bg}`}>
                    {rate7d.label}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {r.pending_retries > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 border border-yellow-600 bg-yellow-50 text-yellow-800 px-2 py-0.5 text-[11px] font-bold"
                      data-testid={`health-pending-${r.destination_id}`}
                    >
                      <Clock size={10} weight="fill" /> {r.pending_retries}
                    </span>
                  ) : (
                    <span className="text-zinc-400 text-[11px]">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-[11px] text-zinc-700">
                  {r.last_attempt ? (
                    <>
                      <div>{relativeTime(r.last_attempt.created_at)}</div>
                      <div className="text-[9px] text-zinc-500">
                        {r.last_attempt.response_code || r.last_attempt.status}
                      </div>
                    </>
                  ) : (
                    <span className="text-zinc-400">no attempts</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <span className="text-[11px] inline-flex items-center gap-1 text-zinc-700 hover:text-black">
                    Drill in <ArrowRight size={10} weight="bold" />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
